import { useState, useEffect, useCallback } from "react";
import {
  Package, Plus, Save, Trash2, Edit2, X, RefreshCw,
  Search, Download, ChevronDown, ChevronUp, Check, Filter
} from "lucide-react";
import getApiUrl from "../../utils/getApiUrl";

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const TABS = [
  { key: "receveur", label: "Receveurs", color: "#3b82f6" },
  { key: "paroi", label: "Parois", color: "#8b5cf6" },
  { key: "meuble", label: "Meubles", color: "#f59e0b" },
  { key: "mpa", label: "MPA", color: "#10b981" },
];

const EMPTY_FORM = {
  nom: "", description: "", marque: "", section: "",
  section_order: 0, order: 0, unite: "Unite",
  prix_ht: 0, tva_rate: 10.0, type_devis: "receveur", actif: true
};

export default function CatalogueProduitsAdmin() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("receveur");
  const [search, setSearch] = useState("");
  const [filterSection, setFilterSection] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/catalogue-produits?type_devis=${activeTab}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (filterSection) url += `&section=${encodeURIComponent(filterSection)}`;
      const r = await authFetch(url);
      if (r.ok) {
        const data = await r.json();
        setItems(data.items || []);
        setStats(data.stats || {});
        setSections(data.sections || []);
        if (!Object.keys(expandedSections).length) {
          const exp = {};
          (data.sections || []).forEach(s => { exp[s] = true; });
          setExpandedSections(exp);
        }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [activeTab, search, filterSection]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.nom.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, type_devis: activeTab };
      const url = editId
        ? `${API}/admin/catalogue-produits/${editId}`
        : `${API}/admin/catalogue-produits`;
      const r = await authFetch(url, {
        method: editId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        setShowForm(false);
        setEditId(null);
        setForm({ ...EMPTY_FORM, type_devis: activeTab });
        await load();
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try {
      await authFetch(`${API}/admin/catalogue-produits/${id}`, { method: "DELETE" });
      await load();
    } catch (e) { console.error(e); }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setForm({
      nom: item.nom || "",
      description: item.description || "",
      marque: item.marque || "",
      section: item.section || "",
      section_order: item.section_order || 0,
      order: item.order || 0,
      unite: item.unite || "Unite",
      prix_ht: item.prix_ht || 0,
      tva_rate: item.tva_rate || 10.0,
      type_devis: item.type_devis || activeTab,
      actif: item.actif !== false,
    });
    setShowForm(true);
  };

  const handleExport = () => {
    window.open(`${API}/admin/catalogue-produits/export?type_devis=${activeTab}`, "_blank");
  };

  const toggleSection = (s) => {
    setExpandedSections(prev => ({ ...prev, [s]: !prev[s] }));
  };

  // Group items by section
  const grouped = {};
  items.forEach(item => {
    const s = item.section || "Sans section";
    if (!grouped[s]) grouped[s] = [];
    grouped[s].push(item);
  });
  const sortedSections = Object.keys(grouped).sort();

  const formatPrice = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? "0.00" : n.toFixed(2);
  };

  return (
    <div className="space-y-4" data-testid="catalogue-produits-admin">
      {/* Tabs */}
      <div className="flex gap-1 bg-slate-800/50 p-1 rounded-lg" data-testid="catalogue-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            data-testid={`tab-${tab.key}`}
            onClick={() => { setActiveTab(tab.key); setSearch(""); setFilterSection(""); }}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === tab.key
                ? "text-white shadow-md"
                : "text-slate-400 hover:text-white hover:bg-slate-700/50"
            }`}
            style={activeTab === tab.key ? { backgroundColor: tab.color } : {}}
          >
            {tab.label}
            <span className="ml-1.5 text-xs opacity-75">({stats[tab.key] || 0})</span>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-500" />
          <input
            data-testid="catalogue-search"
            type="text"
            placeholder="Rechercher par nom, marque..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
          />
        </div>

        {sections.length > 1 && (
          <select
            data-testid="catalogue-filter-section"
            value={filterSection}
            onChange={(e) => setFilterSection(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
          >
            <option value="">Toutes sections</option>
            {sections.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}

        <button
          data-testid="catalogue-export-btn"
          onClick={handleExport}
          className="flex items-center gap-1.5 px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-slate-300"
        >
          <Download className="w-4 h-4" /> CSV
        </button>

        <button
          data-testid="catalogue-add-btn"
          onClick={() => { setEditId(null); setForm({ ...EMPTY_FORM, type_devis: activeTab }); setShowForm(true); }}
          className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm text-white font-medium"
        >
          <Plus className="w-4 h-4" /> Ajouter
        </button>

        <button onClick={load} className="p-2 hover:bg-slate-700 rounded-lg" data-testid="catalogue-refresh-btn">
          <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="bg-slate-800 border border-slate-600 rounded-xl p-4 space-y-3" data-testid="catalogue-form">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-white">{editId ? "Modifier" : "Nouveau"} produit</h3>
            <button onClick={() => { setShowForm(false); setEditId(null); }} className="p-1 hover:bg-slate-700 rounded">
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Nom *</label>
              <input data-testid="form-nom" value={form.nom} onChange={e => setForm(f => ({...f, nom: e.target.value}))}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Marque</label>
              <input data-testid="form-marque" value={form.marque} onChange={e => setForm(f => ({...f, marque: e.target.value}))}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm text-white" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-slate-400 mb-1 block">Description</label>
              <textarea data-testid="form-description" value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                rows={2} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Section</label>
              <input data-testid="form-section" value={form.section} onChange={e => setForm(f => ({...f, section: e.target.value}))}
                list="sections-list"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm text-white" />
              <datalist id="sections-list">
                {sections.map(s => <option key={s} value={s} />)}
              </datalist>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Prix HT</label>
                <input data-testid="form-prix" type="number" step="0.01" value={form.prix_ht}
                  onChange={e => setForm(f => ({...f, prix_ht: parseFloat(e.target.value) || 0}))}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">TVA %</label>
                <input data-testid="form-tva" type="number" step="0.5" value={form.tva_rate}
                  onChange={e => setForm(f => ({...f, tva_rate: parseFloat(e.target.value) || 0}))}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Unité</label>
                <select data-testid="form-unite" value={form.unite} onChange={e => setForm(f => ({...f, unite: e.target.value}))}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm text-white">
                  <option>Unite</option>
                  <option>M2</option>
                  <option>ML</option>
                  <option>Forfait</option>
                  <option>Lot</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" checked={form.actif} onChange={e => setForm(f => ({...f, actif: e.target.checked}))}
                  className="rounded border-slate-600" />
                Actif
              </label>
              <div className="grid grid-cols-2 gap-2 flex-1">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Ordre section</label>
                  <input type="number" value={form.section_order} onChange={e => setForm(f => ({...f, section_order: parseInt(e.target.value) || 0}))}
                    className="w-full px-2 py-1 bg-slate-900 border border-slate-700 rounded text-sm text-white" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Ordre</label>
                  <input type="number" value={form.order} onChange={e => setForm(f => ({...f, order: parseInt(e.target.value) || 0}))}
                    className="w-full px-2 py-1 bg-slate-900 border border-slate-700 rounded text-sm text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => { setShowForm(false); setEditId(null); }}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-slate-300">
              Annuler
            </button>
            <button data-testid="form-save-btn" onClick={handleSave} disabled={saving || !form.nom.trim()}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg text-sm text-white font-medium">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {editId ? "Mettre à jour" : "Créer"}
            </button>
          </div>
        </div>
      )}

      {/* Products list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-slate-500" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-slate-500" data-testid="catalogue-empty">
          <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p>Aucun produit {TABS.find(t => t.key === activeTab)?.label || ""}</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="catalogue-list">
          {sortedSections.map(section => (
            <div key={section} className="bg-slate-800/50 border border-slate-700/50 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection(section)}
                className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-700/30 transition-colors"
              >
                <span className="font-medium text-sm text-slate-200">{section}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">{grouped[section].length} produits</span>
                  {expandedSections[section] ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                </div>
              </button>
              {expandedSections[section] && (
                <div className="border-t border-slate-700/50">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-slate-500 uppercase tracking-wider">
                        <th className="px-4 py-2 text-left">Nom</th>
                        <th className="px-3 py-2 text-left hidden md:table-cell">Marque</th>
                        <th className="px-3 py-2 text-right">Prix HT</th>
                        <th className="px-3 py-2 text-right hidden sm:table-cell">TVA</th>
                        <th className="px-3 py-2 text-center">Unité</th>
                        <th className="px-3 py-2 text-center">Statut</th>
                        <th className="px-3 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grouped[section].map(item => (
                        <tr key={item.id} className="border-t border-slate-700/30 hover:bg-slate-700/20 transition-colors">
                          <td className="px-4 py-2.5">
                            <div className="text-white font-medium truncate max-w-[250px]" title={item.nom}>{item.nom}</div>
                            {item.description && (
                              <div className="text-xs text-slate-500 truncate max-w-[300px]" title={item.description}>{item.description}</div>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-slate-400 hidden md:table-cell">{item.marque || "-"}</td>
                          <td className="px-3 py-2.5 text-right font-mono text-white">{formatPrice(item.prix_ht)} EUR</td>
                          <td className="px-3 py-2.5 text-right text-slate-400 hidden sm:table-cell">{item.tva_rate}%</td>
                          <td className="px-3 py-2.5 text-center text-slate-400">{item.unite}</td>
                          <td className="px-3 py-2.5 text-center">
                            {item.actif !== false ? (
                              <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><Check className="w-3 h-3" /> Actif</span>
                            ) : (
                              <span className="text-xs text-slate-500">Inactif</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => startEdit(item)} className="p-1.5 hover:bg-slate-600 rounded" title="Modifier" data-testid={`edit-${item.id}`}>
                                <Edit2 className="w-3.5 h-3.5 text-blue-400" />
                              </button>
                              <button onClick={() => handleDelete(item.id)} className="p-1.5 hover:bg-red-900/50 rounded" title="Supprimer" data-testid={`delete-${item.id}`}>
                                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
